/**
 * Parse WharfKit TransactArgs / bridge payloads into human-readable actions
 * for the dApp transaction confirmation screen.
 */

import { ABI, APIClient, Bytes, Serializer } from '@wharfkit/antelope'
import { EOS_RPC_ENDPOINTS } from '@properwallet/chain-eos'

export type ParsedTxAction = {
  account: string
  name: string
  data: Record<string, unknown>
  danger: 'transfer' | 'updateauth' | 'voteproducer' | 'linkauth' | 'other'
  /**
   * Hex-serialized action data, when the dApp sent an already-resolved
   * transaction (WebDEX does: Serializer.objectify(resolved.transaction)).
   * `data` is then empty until decodeTxActions() decodes it with the
   * contract's ABI — that's why the confirm dialog used to show bare `{}`.
   */
  rawHex?: string
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null
}

function classifyAction(account: string, name: string): ParsedTxAction['danger'] {
  const n = name.toLowerCase()
  if (n === 'transfer') return 'transfer'
  if (n === 'updateauth') return 'updateauth'
  if (n === 'voteproducer') return 'voteproducer'
  if (n === 'linkauth' || n === 'unlinkauth') return 'linkauth'
  if (account === 'eosio' && (n.includes('auth') || n === 'updateauth')) return 'updateauth'
  return 'other'
}

function normalizeAction(raw: unknown): ParsedTxAction | null {
  const a = asRecord(raw)
  if (!a) return null
  const account = String(a.account ?? a.contract ?? '')
  const name = String(a.name ?? a.action ?? '')
  if (!account && !name) return null
  const data = asRecord(a.data) ?? {}
  const rawHex =
    typeof a.data === 'string' && /^[0-9a-fA-F]*$/.test(a.data) && a.data.length > 0 ? a.data : undefined
  return {
    account: account || '?',
    name: name || '?',
    data,
    danger: classifyAction(account, name),
    rawHex,
  }
}

// ── ABI decoding for already-serialized actions ───────────────────────────────

const abiCache = new Map<string, Promise<ABI | null>>()

function loadAbi(account: string): Promise<ABI | null> {
  let p = abiCache.get(account)
  if (!p) {
    p = (async () => {
      for (const url of EOS_RPC_ENDPOINTS) {
        try {
          const res = await new APIClient({ url }).v1.chain.get_abi(account)
          if (res.abi) return ABI.from(res.abi)
        } catch {
          // try the next endpoint
        }
      }
      return null
    })()
    abiCache.set(account, p)
  }
  return p
}

async function decodeOne(a: ParsedTxAction): Promise<ParsedTxAction> {
  if (!a.rawHex || Object.keys(a.data).length > 0) return a
  const abi = await loadAbi(a.account)
  const type = abi?.actions.find(x => String(x.name) === a.name)?.type
  if (!abi || !type) return a
  try {
    const decoded = Serializer.objectify(
      Serializer.decode({ data: Bytes.from(a.rawHex, 'hex'), abi, type }),
    ) as Record<string, unknown>
    return { ...a, data: decoded ?? {} }
  } catch {
    return a
  }
}

/**
 * Decode hex action data with each contract's on-chain ABI so the user sees
 * "from → to · quantity · memo" instead of `{}`. Never blocks the confirm
 * dialog for long: anything not decoded within `timeoutMs` is shown as-is.
 */
export async function decodeTxActions(actions: ParsedTxAction[], timeoutMs = 4000): Promise<ParsedTxAction[]> {
  if (!actions.some(a => a.rawHex)) return actions
  const all = Promise.all(actions.map(decodeOne))
  const timeout = new Promise<ParsedTxAction[]>(resolve => setTimeout(() => resolve(actions), timeoutMs))
  return Promise.race([all, timeout])
}

/** Service actions that move no funds, with a human explanation. */
export function serviceActionNote(a: ParsedTxAction): string | null {
  if (a.account === 'greymassnoop' && a.name === 'noop') {
    return 'Greymass Fuel — pays CPU/NET for this transaction (no funds move)'
  }
  return null
}

/** Extract actions from TransactArgs, nested transaction, or bridge rest payload. */
export function extractTxActions(args: unknown): ParsedTxAction[] {
  const root = asRecord(args)
  if (!root) return []

  // Direct { actions: [...] }
  if (Array.isArray(root.actions)) {
    return root.actions.map(normalizeAction).filter((a): a is ParsedTxAction => a !== null)
  }

  // { transaction: { actions } } or { transaction: Transaction }
  const tx = asRecord(root.transaction) ?? asRecord(root.tx)
  if (tx && Array.isArray(tx.actions)) {
    return tx.actions.map(normalizeAction).filter((a): a is ParsedTxAction => a !== null)
  }

  // Single action shape
  if (root.account && root.name) {
    const one = normalizeAction(root)
    return one ? [one] : []
  }

  return []
}

export function summarizeActionData(data: Record<string, unknown>): string {
  const keys = ['from', 'to', 'quantity', 'memo', 'account', 'permission', 'parent', 'producer']
  const parts: string[] = []
  for (const k of keys) {
    if (data[k] !== undefined && data[k] !== null && data[k] !== '') {
      parts.push(`${k}: ${String(data[k])}`)
    }
  }
  if (parts.length === 0) {
    try {
      const raw = JSON.stringify(data)
      return raw.length > 120 ? `${raw.slice(0, 117)}...` : raw
    } catch {
      return ''
    }
  }
  return parts.join(' · ')
}

export function actionDangerLabel(danger: ParsedTxAction['danger']): string | null {
  switch (danger) {
    case 'transfer':
      return 'TRANSFER'
    case 'updateauth':
      return 'UPDATEAUTH'
    case 'voteproducer':
      return 'VOTE'
    case 'linkauth':
      return 'LINKAUTH'
    default:
      return null
  }
}
